import { Card, CardAction, CardBadge, Icon, Tooltip } from '@lawpilots/design-system'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useHistory, useViewport } from '@/hooks'
import { LearnerCourseDto } from '@/services/api/tenant/learnerCourses'
import { shortDateByLocale } from '@/utils'
import { MetadataLine, MetadataList } from '../../components/CardMetadata'
import { courseAction } from '../utils/courseAction'
import { isExpiringSoon } from '../utils/isExpiringSoon'

type MyCourseCardProps = {
  course: LearnerCourseDto
  onDownloadCertificate: (course: LearnerCourseDto) => void
}

// A focusable anchor for the tooltip, so the reason is reachable by keyboard as well as by hover.
// The card owns its action buttons, and a disabled button fires no pointer events, so the reason
// an inactive course cannot be opened hangs here rather than on the button itself.
const ReasonHintStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'inherit',
  cursor: 'help',
}

/**
 * One learner course card: title, the duration, how long the course stays available and any
 * earlier completion, then a start/continue/view action and a completion or expiry badge.
 *
 * On a phone the badge moves above the title, the metadata drops to small text, and a finished
 * course offers its certificate rather than a replay.
 */
export const MyCourseCard = ({ course, onDownloadCertificate }: MyCourseCardProps): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const history = useHistory()
  const compact = useViewport() === 'mobile'

  const completed = course.progress === 'completed'
  const inactive = course.availability === 'unavailable'

  // Bottom, not beside the title: a long course name would otherwise force the card wider. The
  // narrow card has no room beside its full-width action, so there the badge goes back on top.
  const position = compact ? 'top' : 'bottom'
  // Access has already gone on an inactive course, so warning that it is about to go says nothing.
  const expiring = !inactive && isExpiringSoon(course.cycle_end_date)
  const badge: CardBadge | undefined = completed
    ? { label: t('my_courses_badge_completed'), tone: 'success', icon: 'check', position }
    : expiring
      ? { label: t('my_courses_badge_expiring'), tone: 'warning', icon: 'warning-circle', position }
      : undefined

  // A finished course has nothing left to choose, so it skips the start / continue screen and
  // replays straight from its stored courseplay URL - the same link that screen would follow.
  //
  // assign, not replace: every screen the learner passes through stays on the back stack, so the
  // browser's Back walks back the way they came. Replacing dropped this list off it entirely.
  const viewCompleted = () => {
    if (course.courseplay_url) window.location.assign(course.courseplay_url)
  }

  const action = courseAction(course)
  const cardAction: CardAction = {
    label: t(action.labelKey),
    icon: action.icon,
    iconPosition: 'right',
    // Start and continue go through the screen that takes the language choice; view does not.
    onClick: completed ? viewCompleted : () => history.push(`/my/courses/${course.course_code}`),
    disabled: action.disabled,
  }

  // The phone card carries one action, and for a finished course the certificate is worth more
  // than a replay. It outlives the course, so an inactive card keeps offering it.
  const downloadable = compact && completed && Boolean(course.certificate_url)
  const downloadAction: CardAction = {
    label: t('cert_download'),
    icon: 'download',
    variant: 'secondary',
    onClick: () => onDownloadCertificate(course),
  }

  const shownAction = downloadable ? downloadAction : cardAction
  const isPrimary = !downloadable && action.variant === 'primary'

  const availableUntil = shortDateByLocale(course.cycle_end_date, i18n.language)
  // Past tense once the cycle is over, but the same date: the window is inclusive, so the course
  // was still open on the day shown. "Ended on" would have to print the day after to be true, and
  // would then give one boundary two different numbers depending on which card you read.
  const availabilityKey = inactive ? 'my_courses_was_available_until' : 'my_courses_available_until'
  const lastCompleted = shortDateByLocale(course.last_completed_at, i18n.language)
  const reason = t('my_courses_unavailable_reason')

  return (
    <Card
      data-testid="my-course-card"
      title={course.course_name}
      badges={badge ? [badge] : undefined}
      primaryAction={isPrimary ? shownAction : undefined}
      secondaryAction={isPrimary ? undefined : shownAction}>
      <MetadataList>
        <MetadataLine icon="clock" compact={compact}>
          {t('my_courses_duration', { count: course.duration_in_minutes })}
        </MetadataLine>
        {availableUntil && (
          <MetadataLine icon="calendar" compact={compact}>
            {t(availabilityKey, { date: availableUntil })}
          </MetadataLine>
        )}
        {/* An earlier completion only explains a course still to be done; the badge covers the rest. */}
        {!completed && lastCompleted && (
          <MetadataLine icon="repeat" compact={compact}>
            {t('my_courses_last_completed', { date: lastCompleted })}
          </MetadataLine>
        )}
        {inactive && (
          <Tooltip content={reason}>
            <Box component="button" type="button" aria-label={reason} sx={ReasonHintStyles}>
              <Icon name="info-circle" size="sm" />
            </Box>
          </Tooltip>
        )}
      </MetadataList>
    </Card>
  )
}
